import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import {
  clearPurposeCodeMessages,
  clearSelectedMcc,
  clearSelectedPurposeCode,
  addPurposeCodeMccMapping,
  removePurposeCodeMccMapping,
  fetchMccByCode,
  fetchMccs,
  fetchPurposeCodeByCode,
  fetchPurposeCodes,
  type Mcc,
  type PurposeCode,
} from "../../features/purposeCode/PurposeCodeSlice";
import CustomModal from "../common/CustomModal";
import PurposeMccHeader from "./components/PurposeMccHeader";
import PurposeCodeListTab from "./components/PurposeCodeListTab";
import MccListTab from "./components/MccListTab";
import PurposeMappingsModalContent from "./components/PurposeMappingsModalContent";
import MccMappingsModalContent from "./components/MccMappingsModalContent";

type ActiveTab = "purpose" | "mcc";

const PurposeCodes: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    purposeCodes,
    mccs,
    loading,
    purposeDetailLoading,
    purposeDetailError,
    mccLoading,
    error,
    mccError,
    mccDetailLoading,
    mccDetailError,
    selectedPurposeCode,
    selectedMcc,
    mappingUpdateLoading,
    mappingUpdateError,
  } = useAppSelector((state) => state.purposeCode);

  const [activeTab, setActiveTab] = useState<ActiveTab>("purpose");
  const [purposeSearch, setPurposeSearch] = useState("");
  const [mccSearch, setMccSearch] = useState("");
  const [selectedPurposeCodeRow, setSelectedPurposeCodeRow] = useState("");
  const [selectedMccRow, setSelectedMccRow] = useState("");
  const [showPurposeModal, setShowPurposeModal] = useState(false);
  const [showMccModal, setShowMccModal] = useState(false);

  const activePurposeCodeForModal =
    selectedPurposeCodeRow || selectedPurposeCode?.purposeCode || "";

  useEffect(() => {
    dispatch(fetchPurposeCodes());
    dispatch(fetchMccs());
  }, [dispatch]);

  const filteredPurposeCodes = useMemo(() => {
    const term = purposeSearch.trim().toLowerCase();
    if (!term) return purposeCodes;
    return purposeCodes.filter(
      (row: PurposeCode) =>
        row.purposeCode.toLowerCase().includes(term) ||
        row.useCase.toLowerCase().includes(term),
    );
  }, [purposeCodes, purposeSearch]);

  const filteredMccs = useMemo(() => {
    const term = mccSearch.trim().toLowerCase();
    if (!term) return mccs;
    return mccs.filter(
      (row: Mcc) =>
        row.mcc.toLowerCase().includes(term) ||
        row.description.toLowerCase().includes(term),
    );
  }, [mccs, mccSearch]);

  const onPurposeClick = async (purposeCode: string) => {
    setSelectedPurposeCodeRow(purposeCode);
    dispatch(clearPurposeCodeMessages());
    dispatch(clearSelectedPurposeCode());
    setShowPurposeModal(true);
    await dispatch(fetchPurposeCodeByCode(purposeCode));
  };

  const onMccClick = async (mcc: string) => {
    setSelectedMccRow(mcc);
    dispatch(clearPurposeCodeMessages());
    dispatch(clearSelectedMcc());
    setShowMccModal(true);
    await dispatch(fetchMccByCode(mcc));
  };

  const handleAddMccMapping = async (mcc: string) => {
    if (!activePurposeCodeForModal || !mcc.trim()) {
      return;
    }
    // avoid duplicate add calls while a mapping update is in-flight
    if (mappingUpdateLoading) {
      return;
    }
    await dispatch(
      addPurposeCodeMccMapping({
        code: activePurposeCodeForModal,
        mcc: mcc.trim(),
      }),
    );
  };

  const handleRemoveMccMapping = async (mcc: string) => {
    if (!activePurposeCodeForModal || !mcc.trim()) {
      return;
    }
    if (mappingUpdateLoading) {
      return;
    }
    await dispatch(
      removePurposeCodeMccMapping({
        code: activePurposeCodeForModal,
        mcc: mcc.trim(),
      }),
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <PurposeMccHeader
          activeTab={activeTab}
          purposeCount={purposeCodes.length}
          mccCount={mccs.length}
          onTabChange={setActiveTab}
        />

        {activeTab === "purpose" ? (
          <PurposeCodeListTab
            loading={loading}
            error={error}
            search={purposeSearch}
            selectedCode={selectedPurposeCodeRow}
            detailLoading={purposeDetailLoading}
            totalCount={purposeCodes.length}
            rows={filteredPurposeCodes}
            onSearchChange={setPurposeSearch}
            onClearSearch={() => setPurposeSearch("")}
            onPurposeClick={onPurposeClick}
          />
        ) : (
          <MccListTab
            loading={mccLoading}
            error={mccError}
            search={mccSearch}
            selectedMcc={selectedMccRow}
            detailLoading={mccDetailLoading}
            totalCount={mccs.length}
            rows={filteredMccs}
            onSearchChange={setMccSearch}
            onClearSearch={() => setMccSearch("")}
            onMccClick={onMccClick}
          />
        )}
      </div>

      <CustomModal
        show={showPurposeModal}
        onHide={() => {
          setShowPurposeModal(false);
          dispatch(clearSelectedPurposeCode());
        }}
        title={`MCC mappings for ${activePurposeCodeForModal}`}
        width="max-w-3xl w-full"
      >
        <PurposeMappingsModalContent
          loading={purposeDetailLoading}
          error={purposeDetailError}
          mccs={selectedPurposeCode?.mccs || {}}
          purposeCode={activePurposeCodeForModal}
          allMccs={mccs}
          onAddMapping={handleAddMccMapping}
          onRemoveMapping={handleRemoveMccMapping}
          mappingUpdateLoading={mappingUpdateLoading}
          mappingUpdateError={mappingUpdateError}
        />
      </CustomModal>

      <CustomModal
        show={showMccModal}
        onHide={() => {
          setShowMccModal(false);
          dispatch(clearSelectedMcc());
        }}
        title={`Purpose code mappings for ${selectedMccRow || selectedMcc?.mcc || ""}`}
        width="max-w-3xl w-full"
      >
        <MccMappingsModalContent
          loading={mccDetailLoading}
          error={mccDetailError}
          purposeCodes={selectedMcc?.purposeCodes || []}
        />
      </CustomModal>
    </div>
  );
};

export default PurposeCodes;
